/** Custom report column catalogue — built from the booking form itself, so new
 *  form fields show up automatically. Tenant renames / switched-off markers come
 *  from lib/formVisibility. */
import { FIELDS } from "@/components/booking-form/registry/fields";
import { TABS } from "@/components/booking-form/registry/tabs";
import {
  ROOT_FIELDS, VEHICLE_REGOS_FIELDS, MASS_LIMITS_FIELDS, LOAD_RESTRAINT_FIELDS, TRAILER_WEIGHTS_FIELDS,
  deriveHydration,
} from "@/lib/booking-fields";

// Slot-driven values (not in the field registry) → the tab they appear on.
const SLOT_TABS = {
  vehicle_type: "vehicle", pallet_type: "restraint", load_rule: "restraint", restraint_method: "restraint",
  cold_chain: "compliance", driver_name: "driver", driver_phone: "driver", driver_licence_type: "driver",
  driver_licence_number: "driver", driver_licence_state: "driver", driver_tba: "driver",
  temp_set_confirmed: "qa", trailer_clean_confirmed: "qa", direction: "schedule",
  pallet_account_in: "qa", pallet_account_out: "qa", pallet_docket_number: "qa",
  contamination_present: "qa", contamination_types: "qa", compliance_temp_confirmed: "qa",
  pallet_temp_between_cartons_c: "qa", sample_pallet1_temp_c: "qa", sample_pallet2_temp_c: "qa",
  damage_pallets: "qa", damage_products: "qa", damage_notes: "qa",
  on_hold: "status", hold_reason: "status", notes: "schedule",
  has_straps: "restraint", has_chains: "restraint",
  weight_load_with_pallets: "vehicle", weight_trailer_1: "vehicle", weight_trailer_2: "vehicle",
};
// Internal / structural keys that make no sense as report columns.
const SKIP = new Set(["qa_confirmations", "reservation_type_id", "bay_id", "site_id", "client_id", "approved_booker_id",
  "assigned_driver_id", "forklift_driver_user_id", "scheduled_start", "scheduled_end", "status", "direction"]);

const human = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
const fmtTime = (iso, tz) => (iso ? new Intl.DateTimeFormat("en-AU", {
  timeZone: tz || "Australia/Melbourne", dateStyle: "short", timeStyle: "short", hour12: false,
}).format(new Date(iso)) : "");

const BASE = [
  { key: "reference_number", label: "Booking ref", tab: "_base" },
  { key: "scheduled_start", label: "Start (site time)", tab: "_base", derive: (r) => fmtTime(r.scheduled_start, r.site?.timezone) },
  { key: "scheduled_end",   label: "End (site time)",   tab: "_base", derive: (r) => fmtTime(r.scheduled_end, r.site?.timezone) },
  { key: "direction", label: "Direction", tab: "_base" },
  { key: "status",    label: "Status",    tab: "_base" },
  { key: "site_name", label: "Site", tab: "_base", derive: (r) => r.site?.name || "" },
  { key: "bay_name",  label: "Bay",  tab: "_base", derive: (r) => r.bay?.name || "" },
  { key: "actual_pallets_in",  label: "Actual pallets IN",  tab: "restraint" },
  { key: "actual_pallets_out", label: "Actual pallets OUT", tab: "restraint" },
];

function buildCatalogue() {
  const reg = new Map(FIELDS.map((f) => [f.key, f]));
  const keys = [
    ...ROOT_FIELDS.map((f) => f.key),
    ...[VEHICLE_REGOS_FIELDS, MASS_LIMITS_FIELDS, LOAD_RESTRAINT_FIELDS, TRAILER_WEIGHTS_FIELDS].flat().map((f) => f.form),
  ];
  const seen = new Set(BASE.map((c) => c.key));
  const cols = [...BASE];
  for (const k of keys) {
    if (seen.has(k) || SKIP.has(k)) continue;
    seen.add(k);
    const f = reg.get(k);
    cols.push({ key: k, label: f?.label || human(k), tab: f?.tab || SLOT_TABS[k] || "other", formField: !!f });
  }
  return cols;
}

export const REPORT_COLUMNS = buildCatalogue();

export const TAB_ORDER = ["_base", ...TABS.map((t) => t.key), "other"];
export const tabTitle = (k, vis) => (k === "_base" ? "Booking" : k === "other" ? "Other"
  : vis.tabLabel(k, TABS.find((t) => t.key === k)?.label || human(k)));

/** Column value for a booking row (hydrates JSONB sub-fields once per row). */
export function cellValue(col, row) {
  if (col.derive) return col.derive(row);
  if (row[col.key] !== undefined) return fmtVal(row[col.key]);
  row.__h ||= deriveHydration(row);
  return fmtVal(row.__h[col.key]);
}

/** Tenant-aware label: rename + "(switched off since …)" for hidden form fields. */
export function columnLabel(col, vis) {
  const label = col.formField ? vis.fieldLabel(col.key, col.label) : col.label;
  if (!col.formField || vis.fieldVisible(col.key)) return label;
  const since = vis.hiddenSince?.(col.key);
  return `${label} (switched off${since ? ` since ${since}` : ""})`;
}
